/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 */

import React from 'react'; // Importing React library to create components
import { Box } from '@mui/material'; // Importing Box component from Material UI for layout

import { Circle, Image, Layer, Rect, Stage, Text } from 'react-konva'; // Importing Konva components for drawing 2D shapes (canvas)
import NotInformation from '../../../components/NotInformation'; // Importing a component that shows when there is no information
import CreateTemplateApp from "../Functionality/CreateTemplateApp"; // Importing the template creation functionality
import CustomModal from "../../../components/Modal/CustomModal"; // Importing a custom modal component
import CustomDescriptionLabel from "../../../components/CustomDescriptionLabel"; // Importing a custom description label component
import { CustomSelect } from "../../../components/CustomSelect"; // Importing a custom select dropdown component

const AddTemplateModal = (props) => {
    const { open, onHide, selectedCards } = props; // Destructuring open, onHide, and selectedCards from props
    const {
        t, // Translation function for text localization
        emptyFields, // To track empty fields
        template, // Template selected for the card
        searchResult, // Search results for templates
        selectedTemplate, // Selected template
        cardWidth, // Width of the card
        cardHeight, // Height of the card
        cardBackgroundColor, // Background color of the card
        handleTemplateChange, // Function to handle template selection
        handleAssign // Function to assign the template to the selected cards
    } = CreateTemplateApp(props); // Destructuring from the CreateTemplateApp custom hook

    return (
        <CustomModal
            title={"assign_template"} // Title of the modal
            show={open} // Whether the modal is visible or not
            size="lg" // Modal size (large)
            buttonConfirm="assign" // Text on the confirm button
            cancelButton={true} // Show cancel button
            confirmButton={true} // Show confirm button
            onConfirm={handleAssign} // Function to call when the confirm button is clicked
            onHide={onHide} // Function to call when the modal is closed
            classNameBody="pt-2"
        >
            <div className='row'>
                {/* Display the number of selected cards */}
                <Box className='col-12 d-flex mb-4'>
                    <CustomDescriptionLabel trans={t('total_cards') + ": "} />
                    <CustomDescriptionLabel trans={(selectedCards ? selectedCards.length : 0)} bold={"normal"} />
                </Box>

                {/* Template selection dropdown */}
                <Box className='col-12'>
                    <CustomSelect
                        id={"slt_atm_template"}
                        name={"template"}
                        value={template} // Current selected template value
                        keyName="id" // Key name for the option's value
                        onChange={(event) => { handleTemplateChange(event); }} // Function to handle template change
                        list={searchResult?.content} // List of templates from search results
                        trans={"template"} // Translation key for the label
                        label={"lbl_atm_template"} // Label for the select dropdown
                        required={true} // Set field as required
                        emptyFields={emptyFields} // Information about empty fields
                        showNoSelectionOption={false} // Whether to show an option for "no selection"
                    />
                </Box>
            </div>

            {/* Section displaying card previews (front and back sides) */}
            <div className={"row"}>
                {selectedTemplate ? (
                    <>
                        {/* Front side preview of the selected template */}
                        <div className={"col-sm-12 col-md-6 col-xxl-6 col-lg-6 p-0 m-0"}>
                            <div className={"col-12 d-flex justify-content-center align-items-center"}>
                                <CustomDescriptionLabel trans={t('front_side')} />
                            </div>
                            <div className={"col-12 d-flex justify-content-center align-items-center"}>
                                <Stage
                                    width={cardWidth + 25} // Card width with some padding
                                    height={cardHeight + 25} // Card height with some padding
                                    scaleX={0.5} // Scaling factor for X-axis
                                    scaleY={0.5} // Scaling factor for Y-axis
                                >
                                    <Layer>
                                        {/* Drawing the background rectangle of the card */}
                                        <Rect
                                            id='background'
                                            width={cardWidth}
                                            height={cardHeight}
                                            fill={cardBackgroundColor} // Card background color
                                        />
                                        {/* Mapping over the template's "combinedObject" and rendering shapes accordingly */}
                                        {JSON.parse(selectedTemplate.raw)[1].combinedObject.map(
                                            (shape, index) => {
                                                if (shape.property === 'squares' || shape.property === 'lines') {
                                                    return (
                                                        <Rect
                                                            key={index}
                                                            x={shape.x} y={shape.y}
                                                            rotation={shape.rotation}
                                                            width={shape.width}
                                                            height={shape.height}
                                                            cornerRadius={shape.cornerRadius}
                                                            stroke={shape.stroke}
                                                            strokeWidth={shape.strokeWidth}
                                                            id={shape.id}
                                                            fill={shape.fill}
                                                        />
                                                    );
                                                } else if (shape.property === 'ellipsis') {
                                                    return (
                                                        <Circle
                                                            key={index}
                                                            x={shape.x - 20} y={shape.y - 10}
                                                            rotation={shape.rotation}
                                                            radius={shape.radius}
                                                            fill={shape.fill}
                                                            stroke={shape.stroke}
                                                            strokeWidth={shape.strokeWidth}
                                                            id={shape.id}
                                                        />
                                                    );
                                                } else if (shape.property === 'texts') {
                                                    return (
                                                        <Text
                                                            key={index}
                                                            x={shape.x - 20} y={shape.y - 10}
                                                            text={shape.content}
                                                            fontSize={shape.fontSize}
                                                            fontFamily={shape.fontFamily}
                                                            fontStyle={shape.fontStyle}
                                                            textDecoration={shape.textDecoration}
                                                            fill={shape.fill}
                                                            align={shape.align}
                                                            width={shape.width}
                                                            height={shape.height}
                                                            wrap={true}
                                                            id={shape.id}
                                                        />
                                                    );
                                                } else if (shape.property === 'resources') {
                                                    const imageObject = new window.Image();
                                                    imageObject.src = shape.base64; // Base64 image
                                                    return (
                                                        <Image
                                                            key={index}
                                                            image={imageObject}
                                                            x={shape.x - 20} y={shape.y - 15}
                                                            rotation={shape.rotation}
                                                            width={shape.width}
                                                            height={shape.height}
                                                            id={shape.id}
                                                        />
                                                    );
                                                }
                                                return null; // Return nothing if shape property is not recognized
                                            }
                                        )}
                                    </Layer>
                                </Stage>
                            </div>
                        </div>

                        {/* Back side preview of the selected template */}
                        <div className={"col-sm-12 col-md-6 col-xxl-6 col-lg-6 p-0 m-0"}>
                            <div className={"col-12 d-flex justify-content-center align-items-center"}>
                                <CustomDescriptionLabel trans={t('back_side')} />
                            </div>
                            <div className={"col-12 d-flex justify-content-center align-items-center"}>
                                <Stage
                                    width={cardWidth + 25} // Card width with padding
                                    height={cardHeight + 25} // Card height with padding
                                    scaleX={0.5} // Scaling factor for X-axis
                                    scaleY={0.5} // Scaling factor for Y-axis
                                    cornerRadius={10} // Rounded corners
                                >
                                    <Layer>
                                        {/* Drawing the background rectangle of the card (back side) */}
                                        <Rect
                                            id='background'
                                            width={cardWidth}
                                            height={cardHeight}
                                            fill={cardBackgroundColor}
                                        />
                                        {/* Mapping over the template's "combinedObject" for the back side */}
                                        {JSON.parse(selectedTemplate.raw)[2].combinedObject.map(
                                            (shape, index) => {
                                                if (shape.property === 'squares' || shape.property === 'lines') {
                                                    return (
                                                        <Rect
                                                            key={index}
                                                            x={shape.x} y={shape.y}
                                                            rotation={shape.rotation}
                                                            width={shape.width}
                                                            height={shape.height}
                                                            cornerRadius={shape.cornerRadius}
                                                            stroke={shape.stroke}
                                                            strokeWidth={shape.strokeWidth}
                                                            id={shape.id}
                                                            fill={shape.fill}
                                                        />
                                                    );
                                                } else if (shape.property === 'ellipsis') {
                                                    return (
                                                        <Circle
                                                            key={index}
                                                            x={shape.x - 20} y={shape.y - 10}
                                                            rotation={shape.rotation}
                                                            radius={shape.radius}
                                                            fill={shape.fill}
                                                            stroke={shape.stroke}
                                                            strokeWidth={shape.strokeWidth}
                                                            id={shape.id}
                                                        />
                                                    );
                                                } else if (shape.property === 'texts') {
                                                    return (
                                                        <Text
                                                            key={index}
                                                            x={shape.x - 20} y={shape.y - 10}
                                                            text={shape.content}
                                                            fontSize={shape.fontSize}
                                                            fontFamily={shape.fontFamily}
                                                            fontStyle={shape.fontStyle}
                                                            textDecoration={shape.textDecoration}
                                                            fill={shape.fill}
                                                            align={shape.align}
                                                            width={shape.width}
                                                            height={shape.height}
                                                            wrap={true}
                                                            id={shape.id}
                                                        />
                                                    );
                                                } else if (shape.property === 'resources') {
                                                    const imageObject = new window.Image();
                                                    imageObject.src = shape.base64; // Base64 image
                                                    return (
                                                        <Image
                                                            key={index}
                                                            image={imageObject}
                                                            x={shape.x - 20} y={shape.y - 15}
                                                            rotation={shape.rotation}
                                                            width={shape.width}
                                                            height={shape.height}
                                                            id={shape.id}
                                                        />
                                                    );
                                                }
                                                return null;
                                            }
                                        )}
                                    </Layer>
                                </Stage>
                            </div>
                        </div>
                    </>
                ) : (
                    // Display a message when no template is selected
                    <div className={"col-12"}> <NotInformation text={t('no_information')} card={true} /></div>
                )}
            </div>
        </CustomModal>
    );
};

export default AddTemplateModal; // Export the AssignCardModal component for use in other parts of the app

